import { useEffect } from "react";

/**
 * Attaches an HLS (.m3u8) source to the given <video> ref.
 *
 * Native pipeline (Safari / iOS) is preferred; everywhere else hls.js is
 * dynamic-imported on first attach so the bundle isn't paid for on browsers
 * that already support HLS.
 *
 * Errors during dynamic import surface in dev (helps spot bundler breakage)
 * and stay silent in prod (a network blip on a non-critical path shouldn't
 * crash the player).
 *
 * @param {React.RefObject<HTMLVideoElement>} videoRef
 * @param {string|null|undefined} hlsUrl
 */
export default function useHls(videoRef, hlsUrl) {
  useEffect(() => {
    if (!videoRef.current || !hlsUrl) return undefined;
    const video = videoRef.current;
    const canPlayNative = video.canPlayType("application/vnd.apple.mpegurl");

    if (canPlayNative) {
      video.src = hlsUrl;
      return () => {
        video.removeAttribute("src");
        video.load();
      };
    }

    let cancelled = false;
    let cleanup = () => {};
    import("hls.js")
      .then(({ default: Hls }) => {
        if (cancelled || !Hls.isSupported()) return;
        const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        cleanup = () => hls.destroy();
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("[useHls] hls.js failed to load:", err);
        }
      });

    return () => {
      cancelled = true;
      cleanup();
    };
    // videoRef is a stable React ref — intentionally omitted. hlsUrl is the
    // only meaningful re-attach trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hlsUrl]);
}
